/**
 * Workspace filters component
 */

import React, { useState } from 'react';
import type { WorkspaceFilterOptions, AccessEvoRegion } from '../types';

interface WorkspaceFiltersProps {
  onFiltersChange: (filters: WorkspaceFilterOptions) => void;
  onClearFilters: () => void;
}

const ACCESS_EVO_REGIONS: AccessEvoRegion[] = ['UNKNOWN', 'EU', 'US', 'UK', 'APAC'];

export function WorkspaceFilters({ onFiltersChange, onClearFilters }: WorkspaceFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [accessEvoRegion, setAccessEvoRegion] = useState<AccessEvoRegion | ''>('');
  const [applicationSubscriptionId, setApplicationSubscriptionId] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [salesforceId, setSalesforceId] = useState('');

  const handleApplyFilters = () => {
    const filters: WorkspaceFilterOptions = {};
    
    if (tenantId.trim()) {
      filters.tenantId = tenantId.trim();
    }
    if (accessEvoRegion) {
      filters.accessEvoRegion = accessEvoRegion;
    }
    if (applicationSubscriptionId.trim()) {
      filters.applicationSubscriptionId = applicationSubscriptionId.trim();
    }
    if (organisationId.trim()) {
      filters.organisationId = organisationId.trim();
    }
    if (salesforceId.trim()) {
      filters.salesforceId = salesforceId.trim();
    }
    
    onFiltersChange(filters);
  };

  const handleClearFilters = () => {
    setTenantId('');
    setAccessEvoRegion('');
    setApplicationSubscriptionId('');
    setOrganisationId('');
    setSalesforceId('');
    onClearFilters();
  };

  const hasActiveFilters = tenantId || accessEvoRegion || applicationSubscriptionId || organisationId || salesforceId;

  return (
    <>
      {/* Filter Card */}
      <div className="filter-card">
        {/* Filter Header */}
        <div 
          className={`filter-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="filter-header-content">
            <h3 className="filter-title">Filter Workspaces</h3>
            <p className="filter-description">Search and filter workspace data</p>
          </div>
          <button className="access-btn access-ghost">
            <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
            <span className="text-caption">{isExpanded ? '▲' : '▼'}</span>
          </button>
        </div>

        {/* Filter Content */}
        <div className={`filter-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="filter-content">
            <div className="access-form">
              <div className="access-form-item">
                <label className="access-label" htmlFor="workspaceTenantId">
                  Tenant ID
                </label>
                <input
                  id="workspaceTenantId"
                  type="text"
                  className="access-input"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="Filter by Tenant ID"
                />
              </div>
              
              <div className="access-form-item">
                <label className="access-label" htmlFor="workspaceRegion">
                  Access Evo Region
                </label>
                <select
                  id="workspaceRegion"
                  className="access-select"
                  value={accessEvoRegion}
                  onChange={(e) => setAccessEvoRegion(e.target.value as AccessEvoRegion | '')}
                >
                  <option value="">All Regions</option>
                  {ACCESS_EVO_REGIONS.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="access-form-item">
                <label className="access-label" htmlFor="workspaceAppSubscriptionId">
                  Application Subscription ID
                </label>
                <input
                  id="workspaceAppSubscriptionId"
                  type="text"
                  className="access-input"
                  value={applicationSubscriptionId}
                  onChange={(e) => setApplicationSubscriptionId(e.target.value)}
                  placeholder="Filter by Application Subscription ID"
                />
              </div>
              
              <div className="access-form-item">
                <label className="access-label" htmlFor="workspaceOrgId">
                  Organization ID
                </label>
                <input
                  id="workspaceOrgId"
                  type="text"
                  className="access-input"
                  value={organisationId}
                  onChange={(e) => setOrganisationId(e.target.value)}
                  placeholder="Filter by Organization ID"
                />
              </div>
              
              <div className="access-form-item">
                <label className="access-label" htmlFor="workspaceSalesforceId">
                  Salesforce ID
                </label>
                <input
                  id="workspaceSalesforceId"
                  type="text"
                  className="access-input"
                  value={salesforceId}
                  onChange={(e) => setSalesforceId(e.target.value)}
                  placeholder="Filter by Salesforce ID"
                />
              </div>
            </div>

            {/* Action Buttons - Right aligned */}
            <div className="filter-actions">
              <button
                onClick={handleClearFilters}
                className="access-btn access-secondary"
              >
                Clear Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="access-btn access-primary"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filter Banner */}
      {hasActiveFilters && (
        <div className="alert alert-info">
          <span>
            Filters applied
          </span>
          <button onClick={handleClearFilters}>
            Clear all filters
          </button>
        </div>
      )}
    </>
  );
}
